import HelperIcon from "components/HelperIcon/HelperIcon";
import RoundTab from "components/RoundTabButtons/RoundTab";
import ToggleButtonComponent from "components/ToggleButton/ToggleButton";
import InfoTooltip from "components/Tooltip/InfoTooltip";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";

import { ColoredButton, WhiteButton } from "../../styledComponents/buttons";
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputTextArea,
  InputWithLabelSection,
  PageTitle,
  RedStar,
} from "../../styledComponents/common";
import { currencySymbolMap } from "../../utils/currencies";
import { checkHtml, checkUrl } from "../../utils/helper";

const LineItemModal = ({
  isOpen,
  toggle,
  addItems,
  action,
  dataForEdit,
  currency,
  totalCalc,
  setTotalCalc,
  pageType = "other",
}) => {
  const { t } = useTranslation();
  const [itemType, setItemType] = useState("product");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState(0);
  const [unitPrice, setUnitPrice] = useState(0);
  const [allowedEvidences, setAllowedEvidences] = useState(false);
  const [tax1Toggle, setTax1Toggle] = useState(false);
  const [tax2Toggle, setTax2Toggle] = useState(false);
  const [saveErrors, setSaveErrors] = useState({});

  useEffect(() => {
    if (action === "edit" && dataForEdit) {
      setItemType(dataForEdit.itemType?.toLowerCase() || "product");
      setName(dataForEdit.name || "");
      setDescription(dataForEdit.description || "");
      setQuantity(dataForEdit.quantity || 0);
      setUnitPrice(dataForEdit.unitPrice || 0);
      setAllowedEvidences(dataForEdit.evidenceToggle || false);
      setTax1Toggle(dataForEdit.tax1Toggle || false);
      setTax2Toggle(dataForEdit.tax2Toggle || false);
    }
  }, [dataForEdit, action]);

  useEffect(() => {
    if (!isOpen) {
      clearStates();
    }
  }, [isOpen]);

  const handleName = e => {
    let errors = { ...saveErrors };
    delete errors["name"];
    setSaveErrors(errors);
    setName(e.target.value);
  };

  const handleQuantity = e => {
    let errors = { ...saveErrors };
    delete errors["quantity"];
    setSaveErrors(errors);
    setQuantity(e.target.value);
  };

  const handleQuantityFocus = e => {
    if (quantity === 0 || quantity === "0") {
      setQuantity("");
    }
  };

  const handleUnitPrice = e => {
    let errors = { ...saveErrors };
    delete errors["unitPrice"];
    setSaveErrors(errors);
    setUnitPrice(e.target.value);
  };

  const handleUnitPriceFocus = e => {
    if (unitPrice === 0 || unitPrice === "0") {
      setUnitPrice("");
    }
  };

  const clearStates = () => {
    setItemType("product");
    setName("");
    setDescription("");
    setQuantity(0);
    setUnitPrice(0);
    setAllowedEvidences(false);
    setTax1Toggle(false);
    setTax2Toggle(false);
    setSaveErrors({});
  };

  const handleCancel = () => {
    clearStates();
    toggle();
  };

  const handleSave = () => {
    if (!checkError()) {
      return;
    }
    const amount = quantity * unitPrice;
    const payload = {
      itemType: itemType.split("")[0].toUpperCase() + itemType.slice(1),
      name,
      description,
      quantity: parseInt(quantity),
      unitPrice: parseFloat(unitPrice).toFixed(2),
      amount: parseFloat(amount).toFixed(2),
      evidenceToggle: allowedEvidences,
    };

    if (pageType === "invoice") {
      payload.tax1Toggle = tax1Toggle;
      payload.tax2Toggle = tax2Toggle;
    }

    if (action == "edit") {
      payload.index = dataForEdit.index;
      payload.action = "edit";

      if (dataForEdit.id) {
        payload.id = dataForEdit.id;
      }
    }

    addItems(payload);
    clearStates();
    toggle();
  };
  const checkError = () => {
    const saveErrors = {};
    let formIsValid = true;

    if (!name.trim()) {
      formIsValid = false;
      saveErrors["name"] = "Name can not be empty.";
    } else if (checkUrl(name)) {
      formIsValid = false;
      saveErrors["name"] = "Name can not contain url.";
    } else if (checkHtml(name)) {
      formIsValid = false;
      saveErrors["name"] = "Invalid name text.";
    }

    if (quantity < 0) {
      formIsValid = false;
      saveErrors["quantity"] = "Quantity can not be negative.";
    }

    if (quantity === "" || quantity === null) {
      formIsValid = false;
      saveErrors["quantity"] = "Quantity should be a number.";
    }

    if (unitPrice < 0) {
      formIsValid = false;
      saveErrors["unitPrice"] = "Price can not be negative.";
    }

    if (unitPrice === "" || unitPrice === null) {
      formIsValid = false;
      saveErrors["unitPrice"] = "Price should be a number.";
    }

    setSaveErrors(saveErrors);
    return formIsValid;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      style={{ marginTop: "10vh", maxWidth: "600px" }}
    >
      <ModalBody style={{ padding: "20px" }}>
        <CommonGrid gap="20px" columns="1fr">
          <CommonFlex disableResponsive gap="4px">
            <PageTitle fontSize="18px">Add Line Item</PageTitle>
            <HelperIcon
              text={t(
                "provide_details_of_the_materials_used_for_the_jobs_for_easier_invoicing",
              )}
            />
          </CommonFlex>
          <InputWithLabelSection>
            <InputLabel>Item type</InputLabel>
            <CommonFlex gap="4px" alignItems="center">
              <RoundTab
                text="Product"
                selected={itemType === "product"}
                onClick={() => setItemType("product")}
                showIcon={true}
              />
              <RoundTab
                text="Service"
                selected={itemType === "service"}
                onClick={() => setItemType("service")}
                showIcon={true}
              />
            </CommonFlex>
          </InputWithLabelSection>

          <InputWithLabelSection>
            <InputLabel>
              Item Name<RedStar>*</RedStar>
            </InputLabel>
            <InputField
              type="text"
              value={name}
              onChange={e => handleName(e)}
              placeholder="Enter item name..."
              style={
                saveErrors &&
                saveErrors[`name`] && {
                  border: `1px solid #fe5969`,
                  backgroundColor: `rgba(252, 87, 104, 0.05)`,
                }
              }
            />
            <FormAlert>{saveErrors["name"]}</FormAlert>
          </InputWithLabelSection>

          <InputWithLabelSection>
            <InputLabel>Description</InputLabel>
            <InputTextArea
              height="100px"
              rows="10"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Enter item description..."
            />
          </InputWithLabelSection>

          <CommonGrid columns="1fr 1fr" gap="20px">
            <InputWithLabelSection>
              <InputLabel>Quantity</InputLabel>
              <InputField
                type="number"
                value={quantity}
                onChange={e => handleQuantity(e)}
                onFocus={handleQuantityFocus}
                placeholder="Enter quantity..."
                style={
                  saveErrors &&
                  saveErrors["quantity"] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              <FormAlert>{saveErrors["quantity"]}</FormAlert>
            </InputWithLabelSection>

            <InputWithLabelSection>
              <InputLabel>
                Unit Price ({currency?.value || currency || "USD"})
              </InputLabel>
              <InputField
                type="number"
                value={unitPrice}
                onChange={e => handleUnitPrice(e)}
                onFocus={handleUnitPriceFocus}
                placeholder="Enter unit price..."
                style={
                  saveErrors &&
                  saveErrors["unitPrice"] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              <FormAlert>{saveErrors["unitPrice"]}</FormAlert>
            </InputWithLabelSection>
          </CommonGrid>

          {quantity > 0 && unitPrice > 0 && (
            <InputWithLabelSection>
              <CommonFlex
                justifyContent="flex-end"
                alignItems="center"
                gap="15px"
              >
                <InputLabel style={{ fontSize: "13px" }}>Amount</InputLabel>
                <CommonText
                  fontWeight="bold"
                  fontSize="13px"
                  style={{ textAlign: "right" }}
                >
                  {currencySymbolMap[currency?.value || currency]}{" "}
                  {(quantity * unitPrice).toFixed(2)}
                </CommonText>
              </CommonFlex>
            </InputWithLabelSection>
          )}

          {pageType === "invoice" && (
            <CommonGrid columns="1fr 1fr" gap="20px">
              <InputWithLabelSection>
                <CommonGrid
                  columns="auto auto"
                  justifyContent="flex-start"
                  alignItems="center"
                  gap="15px"
                >
                  <InputLabel>Tax 1</InputLabel>
                  <CommonFlex>
                    <ToggleButtonComponent
                      active={tax1Toggle}
                      handleClick={() => setTax1Toggle(!tax1Toggle)}
                      width="31px"
                      height="18px"
                      circleSize="10px"
                    />
                  </CommonFlex>
                </CommonGrid>
              </InputWithLabelSection>

              <InputWithLabelSection>
                <CommonGrid
                  columns="auto auto"
                  justifyContent="flex-start"
                  alignItems="center"
                  gap="15px"
                >
                  <InputLabel>Tax 2</InputLabel>
                  <CommonFlex>
                    <ToggleButtonComponent
                      active={tax2Toggle}
                      handleClick={() => setTax2Toggle(!tax2Toggle)}
                      width="31px"
                      height="18px"
                      circleSize="10px"
                    />
                  </CommonFlex>
                </CommonGrid>
              </InputWithLabelSection>
            </CommonGrid>
          )}

          {pageType !== "invoice" && (
            <InputWithLabelSection>
              <CommonGrid
                columns="auto auto"
                justifyContent="flex-start"
                alignItems="center"
                gap="20px"
              >
                <CommonFlex gap="4px">
                  <InputLabel>{t("require_evidences")}</InputLabel>
                  <HelperIcon
                    text={t(
                      "when_enabled_users_will_be_required_to_provide_evidences_for_this_line_item",
                    )}
                  />
                </CommonFlex>
                <CommonFlex>
                  <ToggleButtonComponent
                    active={allowedEvidences}
                    handleClick={() => setAllowedEvidences(!allowedEvidences)}
                    width="31px"
                    height="18px"
                    circleSize="10px"
                  />
                </CommonFlex>
              </CommonGrid>
            </InputWithLabelSection>
          )}

          <CommonFlex justifyContent="flex-end">
            <WhiteButton onClick={handleCancel}>Cancel</WhiteButton>
            <ColoredButton type="orange" onClick={handleSave}>
              Save
            </ColoredButton>
          </CommonFlex>
        </CommonGrid>
      </ModalBody>
    </Modal>
  );
};

export default LineItemModal;
